/**
 * Atlas PM · 用户管理 · API 对接层（P4.3）
 *
 * 对接后端（apps/api/src/modules/user）：
 *   GET    /system/users?page&pageSize&keyword   分页列表
 *   GET    /system/orgs                          组织树
 *   GET    /system/roles                         角色列表
 *   POST   /system/users                         新建
 *   PATCH  /system/users/:id                     编辑（username 不可改）
 *   PATCH  /system/users/:id/status              启用/停用（status 0/1）
 *   POST   /system/users/:id/password            重置口令
 *
 * ⚠️ 字段名以服务端实测为准：
 *   - 状态字段是 `status`（0 停用 / 1 启用），不是 isActive
 *   - roleIds 是**字符串数组**（如 ["7"]），orgId 是字符串
 *   - scopeType：0 全部 / 1 大区 / 2 省 / 3 市 / 4 仅参与
 *   - ⭐ P6.40：数据范围由后端按「角色 + orgId」推导
 *   - ⭐ P7.4：省管理员场景下前端重新提交 scopeOrgIds（多选，可管多个省），
 *     其余角色仍不提交
 */

import { api, qs } from '../../../api/client';

/** 数据范围取值 */
export const SCOPE_TYPES = [0, 1, 2, 3, 4] as const;

/** 数据范围文案 */
export const SCOPE_LABELS: Record<number, string> = {
  0: '全部',
  1: '大区',
  2: '省',
  3: '市',
  4: '仅参与',
};

export function scopeLabel(scopeType: number): string {
  return SCOPE_LABELS[scopeType] ?? `未知(${scopeType})`;
}

/** 用户在某角色上的数据范围（含角色默认范围） */
export interface UserRoleInfo {
  roleId: string;
  code: string;
  name: string;
  /** 用户在角色上的覆盖范围（null = 用角色默认） */
  scopeType: number | null;
  /** 角色默认数据范围 */
  roleScopeType: number;
  scopeOrgIds: string[];
}

/** 用户列表项（与后端 UserView 对齐，绝不含 passwordHash） */
export interface UserItem {
  id: string;
  username: string;
  displayName: string;
  orgId: string;
  orgName: string | null;
  orgCode: string | null;
  isSuperAdmin: boolean;
  /** 0 停用 / 1 启用 */
  status: number;
  lastLoginAt: string | null;
  createdAt: string;
  roles: UserRoleInfo[];
  scopeType: number;
  scopeOrgIds: string[];
}

/** 分页列表载荷 */
export interface UserListResult {
  items: UserItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** 组织树节点 */
export interface OrgNode {
  id: string;
  parentId: string | null;
  name: string;
  code: string;
  /** 1 总部 / 2 大区 / 3 省 / 4 市 */
  level: number;
  regionCode: string | null;
  isSecret: boolean;
  sortOrder: number;
  children: OrgNode[];
}

/** 角色（用户表单选择用） */
export interface RoleItem {
  id: string;
  code: string;
  name: string;
  scopeType: number;
  isBuiltin: boolean;
  permissions: string[];
}

export interface UserListQuery {
  page: number;
  pageSize: number;
  keyword?: string;
  /** 0 停用 / 1 启用；不传 = 全部状态 */
  status?: number;
}

export interface CreateUserInput {
  username: string;
  displayName: string;
  password: string;
  /** 省管理员必传；操作员 / 超管可省略，由后端按建号者组织兜底 */
  orgId?: string;
  roleIds: string[];
  /** 数据范围由后端按角色推导，通常无需传（显式传可覆盖） */
  scopeType?: number;
  scopeOrgIds?: string[];
}

export interface UpdateUserInput {
  displayName?: string;
  orgId?: string;
  roleIds?: string[];
  scopeType?: number;
  scopeOrgIds?: string[];
}

/** 用户管理相关的本地 query key（不污染 api/queryKeys 的公共工厂） */
export const userKeys = {
  all: ['system', 'users'] as const,
  list: (q: UserListQuery) => ['system', 'users', 'list', q] as const,
  orgs: () => ['system', 'orgs'] as const,
  roles: () => ['system', 'roles'] as const,
};

export const userApi = {
  list: (q: UserListQuery) => api.get<UserListResult>(`/system/users${qs({ ...q })}`),

  orgs: () => api.get<OrgNode[]>('/system/orgs'),

  roles: () => api.get<{ items: RoleItem[] }>('/system/roles'),

  create: (input: CreateUserInput) => api.post<UserItem>('/system/users', input),

  update: (id: string, input: UpdateUserInput) =>
    api.patch<UserItem>(`/system/users/${id}`, input),

  setStatus: (id: string, status: number) =>
    api.patch<{ id: string; status: number }>(`/system/users/${id}/status`, { status }),

  resetPassword: (id: string, password: string) =>
    api.post<{ ok: true; id: string }>(`/system/users/${id}/password`, { password }),
};
